/**
 * Read-only display-value resolution for questionnaire fields, shared by the question view
 * (DealViewSurface) and the Scope doc (scope-doc). Pure: field + answers in,
 * a small display descriptor out - each surface renders the descriptor with its own markup
 * (React for the view page, Tiptap JSON for the Scope doc).
 */

import {
  formatCurrencyDisplay,
  integrationDirectionLabel,
  normalizeCurrencyValue,
  parseTechStackSystems,
  planLabel,
  techStackCategoryLabel,
  type ScopeMeta,
} from '@protoss/shared';

import { type SalesDealTemplateFieldSummary } from '../services/salesApi';
import {
  normalizeAnswerChoice,
  normalizeMultiSelectValue,
  type QuestionnaireAnswerMap,
} from './questionnaire-field-visibility';

/** Answer-map key for a field (template fields key by id; ad-hoc fields by fieldKey). */
export function getAnswerKey(field: SalesDealTemplateFieldSummary): string {
  return field.templateFieldId !== null ? String(field.templateFieldId) : field.fieldKey;
}

export type FieldDisplayValue =
  | { kind: 'text'; text: string }
  | { kind: 'pills'; pills: string[] }
  | {
      kind: 'table';
      rows: Record<string, string>[];
      columns: Array<{ key: string; label: string }>;
    }
  | { kind: 'empty' };

export function resolveFieldDisplayValue(
  field: SalesDealTemplateFieldSummary,
  answers: QuestionnaireAnswerMap
): FieldDisplayValue {
  const raw = answers[getAnswerKey(field)] ?? field.value ?? null;

  if (field.uiControl === 'table') {
    const columns = (field.displayHints?.columns as Array<{ key: string; label: string }>) ?? [];
    const rows = Array.isArray(raw)
      ? (raw as Array<Record<string, string>>).filter((row) =>
          Object.values(row).some((v) => typeof v === 'string' && v.trim())
        )
      : [];
    if (rows.length === 0) return { kind: 'empty' };
    return { kind: 'table', rows, columns };
  }

  if (
    field.uiControl === 'multi_select' ||
    field.uiControl === 'pill_select' ||
    field.uiControl === 'pill_dropdown' ||
    field.uiControl === 'tag_input'
  ) {
    // Annotated multi_select stores objects with a .value property
    let values: string[];
    if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === 'object' && raw[0] !== null) {
      values = (raw as Array<{ value?: string }>)
        .map((v) => (typeof v.value === 'string' ? v.value : ''))
        .filter(Boolean);
    } else {
      values = normalizeMultiSelectValue(raw);
    }
    if (values.length === 0) return { kind: 'empty' };
    const pills = values.map((v) => {
      const option = field.options.find((o) => o.value === v);
      return option?.label ?? v;
    });
    return { kind: 'pills', pills };
  }

  if (field.uiControl === 'tech_stack_editor') {
    const systems = parseTechStackSystems(raw).filter((system) => system.name.trim().length > 0);
    if (systems.length === 0) return { kind: 'empty' };
    return {
      kind: 'table',
      columns: [
        { key: 'technology', label: 'Technology' },
        { key: 'category', label: 'Category' },
        { key: 'plan', label: 'Plan' },
        { key: 'scope', label: 'Scope' },
        { key: 'integration', label: 'Integration' },
      ],
      rows: systems.map((system) => ({
        technology: system.name,
        category: techStackCategoryLabel(system.category),
        plan: planLabel(system.plan),
        scope: system.inScope ? 'In scope' : 'Out of scope',
        integration:
          system.inScope && system.integration
            ? `${system.integration.hours}h · ${integrationDirectionLabel(
                system.integration.direction
              )}${system.integration.type === 'custom' ? ' · Custom' : ''}`
            : '-',
      })),
    };
  }

  if (
    field.uiControl === 'select' ||
    field.uiControl === 'pill_radio' ||
    field.uiControl === 'stack_option'
  ) {
    const value = normalizeAnswerChoice(raw);
    if (!value) return { kind: 'empty' };
    const option = field.options.find((o) => o.value === value);
    const label = option?.label ?? value;
    return { kind: 'pills', pills: [label] };
  }

  if (field.uiControl === 'currency') {
    // Coerce defensively: legacy/garbage values (e.g. the literal "[object Object]")
    // normalize to empty rather than rendering broken text.
    const text = formatCurrencyDisplay(normalizeCurrencyValue(raw));
    return text ? { kind: 'text', text } : { kind: 'empty' };
  }

  if (field.uiControl === 'number' || field.uiControl === 'slider') {
    if (typeof raw === 'number' && !Number.isNaN(raw)) {
      return { kind: 'text', text: raw.toLocaleString('en-US') };
    }
    if (typeof raw === 'string' && raw.trim()) {
      const n = parseFloat(raw.replace(/,/g, ''));
      if (Number.isFinite(n)) return { kind: 'text', text: n.toLocaleString('en-US') };
      return { kind: 'text', text: raw.trim() };
    }
    return { kind: 'empty' };
  }

  if (field.uiControl === 'date') {
    const dateStr = typeof raw === 'string' ? raw.trim() : '';
    if (!dateStr) return { kind: 'empty' };
    try {
      // Append time so it's parsed in local timezone, not UTC
      const d = new Date(`${dateStr}T00:00:00`);
      if (Number.isNaN(d.getTime())) return { kind: 'text', text: dateStr };
      return {
        kind: 'text',
        text: d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
      };
    } catch {
      return { kind: 'text', text: dateStr };
    }
  }

  // text, textarea, autocomplete, user_select, month_day, tag_input, etc.
  const text = typeof raw === 'string' ? raw.trim() : typeof raw === 'number' ? String(raw) : '';
  if (!text) return { kind: 'empty' };
  return { kind: 'text', text };
}

export function hasFieldValue(dv: FieldDisplayValue): boolean {
  return dv.kind !== 'empty';
}

/** Whether an ad-hoc field's scope brief carries anything worth showing read-only. */
export function hasScopeBriefContent(scope?: ScopeMeta): boolean {
  if (!scope) return false;
  if (
    scope.scopeStatus === 'mentioned' ||
    scope.scopeStatus === 'ongoing' ||
    scope.workType ||
    scope.problem ||
    scope.outcome
  ) {
    return true;
  }
  if (scope.notes) return true;
  return Boolean(
    scope.complexityDrivers?.length ||
    scope.inScope?.length ||
    scope.outOfScope?.length ||
    scope.assumptions?.length
  );
}

/** A field is shown in "answered only" mode if it has an answer OR a scope brief. */
export function fieldHasVisibleContent(
  field: SalesDealTemplateFieldSummary,
  answers: QuestionnaireAnswerMap
): boolean {
  return (
    hasFieldValue(resolveFieldDisplayValue(field, answers)) || hasScopeBriefContent(field.scopeMeta)
  );
}
